import { ChevronLeft, ChevronRight } from "lucide-react";

import { getPaginationRange } from "@/utils/pagination-range";

type PaginationControlsProps = {
  currentPage: number;
  lastPage: number;
  paginations: (number | string)[];
  totalItems: number;
  itemsPerPage: number;
  onGoToPage: (action: number | "prev" | "next") => void;
  showSummary?: boolean;
  showPageButtons?: boolean;
};

export function PaginationControls({
  currentPage,
  lastPage,
  paginations,
  totalItems,
  itemsPerPage,
  onGoToPage,
  showSummary = true,
  showPageButtons = true,
}: PaginationControlsProps) {
  if (totalItems === 0) return null;

  const effectiveLastPage =
    lastPage > 1 ? lastPage : Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const hasMultiplePages = totalItems > itemsPerPage;
  const { start, end } = getPaginationRange(currentPage, itemsPerPage, totalItems);

  const pageButtons =
    showPageButtons && hasMultiplePages ? (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onGoToPage("prev")}
        disabled={currentPage === 1}
        aria-label="Página anterior"
        className="rounded border bg-white px-3 py-1 text-black transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </button>

      {paginations.length > 0 ? (
        paginations.map((page, idx) =>
          page === "..." ? (
            <span
              key={`ellipsis-${idx}`}
              className="px-2 text-muted-foreground"
              aria-hidden="true"
            >
              …
            </span>
          ) : (
            <button
              key={page}
              type="button"
              onClick={() => onGoToPage(Number(page))}
              aria-label={`Página ${page}`}
              aria-current={page === currentPage ? "page" : undefined}
              className={`rounded border px-3 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                page === currentPage
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-white text-black hover:bg-neutral-50"
              }`}
            >
              {page}
            </button>
          )
        )
      ) : (
        <span className="px-2 text-sm tabular-nums text-gray-700">
          {currentPage} / {effectiveLastPage}
        </span>
      )}

      <button
        type="button"
        onClick={() => onGoToPage("next")}
        disabled={currentPage >= effectiveLastPage}
        aria-label="Próxima página"
        className="rounded border bg-white px-3 py-1 text-black transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  ) : null;

  if (!showSummary) {
    return pageButtons;
  }

  if (!showPageButtons) {
    return (
      <p className="text-xs tabular-nums text-muted-foreground">
        {`Mostrando ${start} - ${end} de ${totalItems} registros`}
      </p>
    );
  }

  return (
    <div className="flex w-full flex-col items-center justify-between gap-3 sm:flex-row">
      <p className="text-xs tabular-nums text-muted-foreground">
        {`Mostrando ${start} - ${end} de ${totalItems} registros`}
      </p>
      {pageButtons}
    </div>
  );
}
